import { useEnvironmentVariables, useRuntimeVariables } from 'app-kit'
import {
  AGENTIC_SEARCH,
  DOC_PAGE_LOAD,
  DOC_SEARCH_DWELL,
  DOC_SEARCH_QUERY,
  DOC_SEARCH_RESULTS,
  DOC_SEARCH_SELECT,
  TIME_SERIES_VIEW,
} from 'features/analytics'
import { useGeckoAuth } from 'features/auth'
import {
  PowerAnalyticsEntityContext,
  usePowerAnalyticsEntityContext,
} from 'features/knowledgeDocumentViewer/contexts/PowerAnalyticsContext'
import { usePowerAnalyticsListener } from 'features/knowledgeDocumentViewer/contexts/PowerAnalyticsListenerContext'
import type { DocumentSearchMode } from 'features/knowledgeDocumentViewer/types'
import { useGeckoPermissions } from 'features/permissions/hooks/useGeckoPerms'
import { useCallback } from 'react'
import type {
  DocumentsSearchParams,
  DocumentsViewerContentFilter,
} from 'routes/documents/-searchParams'

const POWER_ANALYTICS_EVENTS_PATH = '/api/v2/analytics/events'

const SESSION_ID_KEY = 'power_analytics_session_id'

export function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY)
    if (!id) {
      id = crypto.randomUUID()
      sessionStorage.setItem(SESSION_ID_KEY, id)
    }
    return id
  } catch {
    return crypto.randomUUID()
  }
}

export type DocumentViewerSearchUrlFilterFields = Pick<
  DocumentsSearchParams,
  | 'searchFilterTitle'
  | 'searchFilterDocumentTitle'
  | 'searchFilterSystem'
  | 'searchFilterDrawing'
  | 'searchFilterVendor'
  | 'searchFilterDocType'
>

export type DocSearchQueryPowerAnalyticsProperties = {
  query: string
  browse_all: boolean
  content_filter: DocumentsViewerContentFilter
  site_id: string
  search_mode: DocumentSearchMode
} & DocumentViewerSearchUrlFilterFields

export type DocSearchSelectPowerAnalyticsProperties = {
  document_id: string
  rank_unfiltered: number
  rank_filtered: number
  source?: string
  score?: number
  lexical_weight?: number
  semantic_weight?: number
  query: string
  site_id: string
  open_in_new_tab?: boolean
  is_linked_docs_mode?: boolean
} & DocumentViewerSearchUrlFilterFields

export type DocSearchResultsPowerAnalyticsProperties = {
  query: string
  site_id: string
  result_count: number
  search_latency_ms?: number
  search_mode: DocumentSearchMode
  conversation_id?: string
  is_fallback?: boolean
}

/**
 * Agentic-only search telemetry. Emitted once per completed agentic turn,
 * alongside the shared `Doc Search Results` event, so the query plan weights
 * and fallback status are queryable without parsing the generic results event.
 */
export type AgenticSearchPowerAnalyticsProperties = {
  query: string
  site_id: string
  conversation_id: string
  is_fallback: boolean
  result_count: number
  elasticsearch_weight: number
  pinecone_weight: number
  search_latency_ms?: number
}

export type DocSearchDwellPowerAnalyticsProperties = {
  document_id: string
  dwell_duration_ms: number
  query: string
  site_id: string
}

/** How a sensor came to be on the chart (relative to the dossier's primary). */
export type TimeSeriesViewOrigin = 'primary' | 'related' | 'searched' | 'manual'

/**
 * A historian time series (sensor/pen) the user looked at in the viewer. Entity
 * context (dossier/asset/etc.) is passed explicitly here because this event is
 * emitted from the page, outside the viewer's entity-context provider.
 */
export type TimeSeriesViewPowerAnalyticsProperties = {
  /** Rolodex `datapoints` entry id of the viewed stream. */
  datapoint_id?: string
  /** Human/tag label for the stream, when known. */
  name?: string
  origin: TimeSeriesViewOrigin
  query?: string
} & PowerAnalyticsEntityContext

export type SendPowerAnalyticsEvent = {
  (
    eventName: typeof DOC_SEARCH_QUERY.title,
    properties: DocSearchQueryPowerAnalyticsProperties
  ): void
  (
    eventName: typeof DOC_SEARCH_SELECT.title,
    properties: DocSearchSelectPowerAnalyticsProperties
  ): void
  (
    eventName: typeof DOC_SEARCH_RESULTS.title,
    properties: DocSearchResultsPowerAnalyticsProperties
  ): void
  (
    eventName: typeof AGENTIC_SEARCH.title,
    properties: AgenticSearchPowerAnalyticsProperties
  ): void
  (
    eventName: typeof DOC_SEARCH_DWELL.title,
    properties: DocSearchDwellPowerAnalyticsProperties,
    options?: { keepalive?: boolean }
  ): void
  (
    eventName: typeof TIME_SERIES_VIEW.title,
    properties: TimeSeriesViewPowerAnalyticsProperties
  ): void
  (
    eventName: typeof DOC_PAGE_LOAD.title,
    properties: Record<string, unknown>
  ): void
}

export type PowerAnalyticsEventProperties =
  | DocSearchQueryPowerAnalyticsProperties
  | DocSearchSelectPowerAnalyticsProperties
  | DocSearchResultsPowerAnalyticsProperties
  | AgenticSearchPowerAnalyticsProperties
  | DocSearchDwellPowerAnalyticsProperties
  | TimeSeriesViewPowerAnalyticsProperties
  | Record<string, unknown>

export function analyticsEnvironmentLabel(
  isLocal: boolean,
  isDev: boolean,
  isProd: boolean
): 'local' | 'dev' | 'prod' {
  if (isLocal) return 'local'
  if (isDev) return 'dev'
  if (isProd) return 'prod'
  return 'dev'
}

export function usePowerAnalyticsBackend(siteId: string) {
  const { authorizedAxios, authorization } = useGeckoAuth()
  const { POWER_BACKEND_URL } = useRuntimeVariables()
  const { IS_LOCAL, IS_DEV, IS_PROD } = useEnvironmentVariables()
  const { user: currentUser } = useGeckoPermissions()
  const userEmail = currentUser?.email
  const listener = usePowerAnalyticsListener()
  // Entity context (dossier/asset/etc.) the embedder injects so every event is
  // attributable to what the user is investigating; undefined off the Dossiers
  // page, leaving the standalone viewer's payloads unchanged.
  const entityContext = usePowerAnalyticsEntityContext()

  const buildPayload = useCallback(
    (properties: PowerAnalyticsEventProperties): Record<string, unknown> => {
      const payload: Record<string, unknown> = {
        ...properties,
        ...(entityContext ?? {}),
        user_email: userEmail,
        site_id: siteId,
        session_id: getOrCreateSessionId(),
        environment: analyticsEnvironmentLabel(IS_LOCAL, IS_DEV, IS_PROD),
      }
      if (typeof window !== 'undefined') {
        payload.page_url = window.location.href
      }
      return payload
    },
    [entityContext, userEmail, siteId, IS_LOCAL, IS_DEV, IS_PROD]
  )

  const sendPowerAnalyticsEvent: SendPowerAnalyticsEvent = useCallback(
    (
      eventName: string,
      properties: PowerAnalyticsEventProperties,
      options?: { keepalive?: boolean }
    ) => {
      // Fan out to any embedder listener first (independent of backend send,
      // so it fires even before the user is identified). Never let it throw.
      try {
        listener?.(eventName, properties as Record<string, unknown>)
      } catch {
        // a misbehaving listener must not break telemetry
      }

      if (!userEmail) return
      const url = `${POWER_BACKEND_URL}${POWER_ANALYTICS_EVENTS_PATH}`
      const payload = {
        event_name: eventName,
        properties: buildPayload(properties),
      }

      if (options?.keepalive) {
        try {
          void fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(authorization ? { Authorization: authorization } : {}),
            },
            body: JSON.stringify(payload),
            keepalive: true,
          }).catch(() => {})
        } catch {
          // fetch may throw during unload in some browsers
        }
        return
      }

      void authorizedAxios.post(url, payload).catch(() => {})
    },
    [
      listener,
      userEmail,
      POWER_BACKEND_URL,
      authorization,
      authorizedAxios,
      buildPayload,
    ]
  )

  return { sendPowerAnalyticsEvent }
}
